import {
  Card,
  Margin,
  P,
  Radio,
  RadioField,
  SelectField,
  TextLink,
} from '@ovotech/nebula';
import {
  PrimaryCTAButton as ElementPrimaryCTAButton,
  SecondaryCTAButton as ElementSecondaryCTAButton,
} from '@ovotech/element-v4';
import { AnalyticsClick, useAnalytics } from '@ovotech/ui-tools';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RequiredFieldsError } from '../../types';
import { ReactComponent as EVCarIcon } from '@/src/resources/car.svg';
import { ReactComponent as LinkIcon } from '@/src/resources/link.svg';
import { ReactComponent as SolarPanelIcon } from '@/src/resources/solar-panel.svg';
import ChevronRight from '@/src/resources/chargeAnytime/chevron-right.svg';
import { mqMediumDown, mqSmallDown } from '@/src/utils/mediaQuery';
import {
  COMPATIBLE_EV_CHARGERS_LINK,
  EV,
  isNotListedSelected,
  useChargeAnytimeContext,
  useChargeAnytimeDevices,
  useEligibilityQuery,
  useVehicleOptions,
  VEHICLE_ENQUIRY_LINK,
  yearOptions,
  findMatchingVehicle,
} from '@monovo/ev-anytime';
import { PageHeader } from '@/src/pages/AnytimeEligibilityV2/components/PageHeader';
import { PageWithNavigation } from '@/src/components';
import { useHistory } from 'react-router-dom-v5';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import { SectionHeader } from '@/src/pages/AnytimeEligibilityV2/components/SectionHeader';

const SectionContainer = styled.div`
  margin-bottom: 25px;
`;

const SectionDivider = styled.div`
  ${({ theme: { space } }) => `
    height: 0;
    width: 100%;
    margin: ${space[6]} 0;
    border-bottom: 1px solid #D1D6DE
  `}
`;

const StyledP = styled(P)`
  color: #2f3749;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;
  margin-top: ${({ theme: { space } }) => space[6]};

  ${mqMediumDown(`
    flex-direction: column-reverse;
  `)};
`;

const StyledElementPrimaryCTAButton = styled(ElementPrimaryCTAButton)`
  ${({ theme: { colors } }) => `
    background-color: ${colors.roundalDark};
  `}
`;

const ButtonIcon = styled.img`
  display: inline;
  width: 16px;
  height: 16px;
  vertical-align: sub;
  margin-left: 5px;

  ${mqSmallDown(`
    display: none;
  `)};
`;

const containsTrueValue = (values: Record<string, boolean | undefined>) =>
  Object.values(values).some(v => v);

export const VehicleAndHomeSetupQuestions = () => {
  const history = useHistory();
  const { dispatch: analyticsDispatch } = useAnalytics();
  const { modelOptions, makeOptions, isLoading, isError, getVehicleInfo } =
    useVehicleOptions();
  const { data: vehiclesData } = useChargeAnytimeDevices('vehicles');
  const vehicleDevices = vehiclesData?.devices ?? [];

  const {
    smartCharger,
    make,
    setMake,
    model,
    setModel,
    year,
    setYear,
    vehicleDeviceId,
    setVehicleDeviceId,
    setType,
    hasInternetAccess,
    setHasInternetAccess,
    hasSolarPanels,
    setHasSolarPanels,
  } = useChargeAnytimeContext();

  useEffect(() => {
    if (!smartCharger) {
      history.replace(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER);
    }
  }, [smartCharger, history]);

  const { refetch: checkEligibility, isFetching: isFetchingEligibility } =
    useEligibilityQuery();

  const [eligibilityCheckFailed, setEligibilityCheckFailed] = useState(false);
  const [formErrors, setFormErrors] = useState<RequiredFieldsError>({
    make: false,
    model: false,
    year: false,
    hasSolarPanels: false,
    hasInternetAccess: false,
  });

  const selectedVehicleIsBeta = useMemo(() => {
    if (!vehicleDeviceId || !year) return false;
    const matchingVehicle = findMatchingVehicle(
      vehicleDevices,
      vehicleDeviceId,
      year,
    );
    return matchingVehicle?.status === 'BETA_COMPATIBLE';
  }, [vehicleDeviceId, year, vehicleDevices]);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY
        .EVS_OVO_CHARGE_ANYTIME_ELIGIBILITY_HOME_SET_UP_VIEWED,
      properties: { chargerName: smartCharger },
    });
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCharger]);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: 'EVS - EVS OVO Charge Anytime D2V Home Set Up Viewed',
      properties: { chargerName: smartCharger },
    });
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartCharger]);

  const models = useMemo(() => {
    if (!make) return [];
    return modelOptions(make);
  }, [make, modelOptions]);

  const isMakeListed = !isNotListedSelected(make);
  const isModelListed = !isNotListedSelected(model);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const errors = {
      make: !make,
      model: isMakeListed && !model,
      year: isMakeListed && isModelListed && !year,
      hasSolarPanels: hasSolarPanels === null,
      hasInternetAccess: hasInternetAccess === null,
    };

    const formHasErrors = containsTrueValue(errors);

    setFormErrors(errors);

    if (formHasErrors) return;

    const { data: eligibility, isError: isEligibilityError } =
      await checkEligibility();

    if (isEligibilityError || !eligibility) {
      setEligibilityCheckFailed(true);
      return;
    }

    const isEligible =
      eligibility.compatibilityStatus === 'COMPATIBLE' ||
      eligibility.compatibilityStatus === 'BETA_COMPATIBLE';
    const reasonsForIncompatibility =
      eligibility?.compatibilityStatus === 'INCOMPATIBLE'
        ? eligibility.reasonsForIncompatibility
        : [];

    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY
        .EVS_OVO_CHARGE_ANYTIME_ELIGIBILITY_HOME_SET_UP_SUBMIT_CLICKED,
      properties: {
        smartChargerSelection: smartCharger,
        make: make,
        model: model,
        year: year,
        vehicleDeviceId: vehicleDeviceId,
        internetAccess: hasInternetAccess,
        solarPanels: hasSolarPanels,
      },
    });

    analyticsDispatch({
      type: 'click',
      name: 'EVS - EVS OVO Charge Anytime D2V Home Set Up submit clicked',
      properties: {
        make,
        model,
        year,
        vehicleDeviceId,
        hasInternetAccess,
        hasSolarPanels,
      },
    });

    analyticsDispatch({
      type: 'view',
      name: isEligible
        ? EV.ELIGIBILITY.EVS_OVO_CHARGE_ANYTIME_CONFIRMATION_ELIGIBILITY_VIEWED
        : EV.ELIGIBILITY
            .EVS_OVO_CHARGE_ANYTIME_CONFIRMATION_INELIGIBILITY_VIEWED,
      properties: {
        smartChargerSelection: smartCharger,
        make,
        model,
        year,
        vehicleDeviceId,
        eligibleVehicle: reasonsForIncompatibility.includes(
          'IncompatibleVehicle',
        ),
        hasSolarPanels,
        hasInternetAccess,
        hasSmartMeter: reasonsForIncompatibility.includes('NoSmartMeter'),
        hasOvoElectricity: reasonsForIncompatibility.includes(
          'NoElectricityTariff',
        ),
      },
    });

    analyticsDispatch({
      type: 'view',
      name: isEligible
        ? 'EVS - EVS OVO Charge Anytime D2V Outcome eligible Viewed'
        : 'EVS - EVS OVO Charge Anytime D2V Outcome Ineligible Viewed',
      properties: {
        smartChargerSelection: smartCharger,
        carMake: make,
        carModel: model,
        carYear: year,
        vehicleDeviceId,
        internetAccess: hasInternetAccess,
        solarPanels: hasSolarPanels,
      },
    });

    if (isEligible) {
      history.push(ROUTE_ANYTIME_ELIGIBILITY.PRODUCT_SELECTION);
    } else {
      // Not eligible at all
      history.push(ROUTE_ANYTIME_ELIGIBILITY.FALLOUT);
    }
  };

  const onMakeValueChange = (value: string | undefined) => {
    if (value && formErrors.make) {
      setFormErrors(existingErrors => ({ ...existingErrors, make: false }));
    }

    setMake(value ?? null);
    setModel(null);
    setYear(null);
    setVehicleDeviceId(null);
  };

  const onModelValueChange = (vehicleDeviceId: string | undefined) => {
    if (vehicleDeviceId && formErrors.model) {
      setFormErrors(existingErrors => ({ ...existingErrors, model: false }));
    }

    if (!isNotListedSelected(vehicleDeviceId ?? '')) {
      const vehicle = getVehicleInfo(vehicleDeviceId ?? '');
      setModel(vehicle?.model ?? null);
      setType(vehicle?.type ?? null);
    } else {
      setModel(vehicleDeviceId ?? null);
    }
    setVehicleDeviceId(vehicleDeviceId ?? null);
    setYear(null);
  };

  const onYearValueChange = (value: number | undefined) => {
    if (value && formErrors.year) {
      setFormErrors(existingErrors => ({ ...existingErrors, year: false }));
    }
    setYear(value ?? null);
  };

  const onHasInternetAccessValueChange = (value: boolean) => {
    if (formErrors.hasInternetAccess) {
      setFormErrors(existingErrors => ({
        ...existingErrors,
        hasInternetAccess: false,
      }));
    }
    setHasInternetAccess(value);
  };

  const onHasSolarPanelsValueChange = (value: boolean) => {
    if (formErrors.hasSolarPanels) {
      setFormErrors(existingErrors => ({
        ...existingErrors,
        hasSolarPanels: false,
      }));
    }
    setHasSolarPanels(value);
  };

  if (isError || eligibilityCheckFailed) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  if (isLoading || isFetchingEligibility) {
    return <Loading title={'Checking eligibility'} />;
  }

  const yearOfManufactureHint = (
    <>
      <P style={{ color: 'GrayText' }}>
        If you're unsure, use{' '}
        <TextLink opensInNewWindow href={VEHICLE_ENQUIRY_LINK}>
          this tool
        </TextLink>{' '}
        to find out.
      </P>
    </>
  );
  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <PageHeader
        title="Check your set-up"
        showDescription={false}
        testId="check-eligibility-header"
        titleLevel="h4"
      />
      <form onSubmit={onSubmit}>
        <Card data-testid="eligibility-questions-body">
          <SectionContainer>
            <SectionHeader Icon={EVCarIcon}>What EV do you have?</SectionHeader>
            <StyledP>
              At the moment, Charge Anytime only works with some EVs. We’re
              always working on adding more.
            </StyledP>
            <Margin vertical={3}>
              <SelectField
                id="make"
                name="make"
                label="Make"
                value={make ?? ''}
                error={formErrors.make && 'Please select a vehicle make'}
                onValueChange={onMakeValueChange}
                data-testid="make-dropdown"
              >
                <option disabled value="">
                  Select make...
                </option>
                {makeOptions.map(make => (
                  <option id={make.value} value={make.value} key={make.value}>
                    {make.label}
                  </option>
                ))}
              </SelectField>
            </Margin>
            {isMakeListed && (
              <Margin vertical={3}>
                <SelectField
                  id="model"
                  name="model"
                  label="Model"
                  value={vehicleDeviceId ?? ''}
                  error={formErrors.model && 'Please select a vehicle model'}
                  onValueChange={onModelValueChange}
                  data-testid="model-dropdown"
                >
                  <option disabled value="">
                    Select model...
                  </option>
                  {models.map(model => (
                    <option
                      id={model.value}
                      value={model.value}
                      key={model.value}
                    >
                      {model.label}
                    </option>
                  ))}
                </SelectField>
                {selectedVehicleIsBeta && (
                  <Margin top={2}>
                    <P style={{ fontSize: '14px', color: 'GrayText' }}>
                      This device is currently in Beta testing. You may
                      experience difficulty connecting and charging while we
                      continue to improve the integration with this model.
                    </P>
                  </Margin>
                )}
              </Margin>
            )}
            {isMakeListed && isModelListed && (
              <Margin vertical={3}>
                <SelectField
                  id="year"
                  name="year"
                  label="Year of manufacture"
                  hint={yearOfManufactureHint}
                  value={year ? `${year}` : ''}
                  error={
                    formErrors.year && 'Please select the year of manufacture'
                  }
                  onValueChange={year => onYearValueChange(Number(year))}
                  data-testid="year-dropdown"
                >
                  <option disabled value="">
                    Select year...
                  </option>
                  {yearOptions.map(option => (
                    <option
                      id={`${option.value}`}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </SelectField>
              </Margin>
            )}
          </SectionContainer>

          <SectionContainer>
            <SectionHeader Icon={LinkIcon}>
              Can your EV access the internet when you charge at home via Wi-Fi
              or mobile data?
            </SectionHeader>

            <StyledP>
              Your EV needs an internet connection to communicate with Charge
              Anytime.
            </StyledP>
            <RadioField
              label=""
              name="hasInternetAccess"
              error={
                formErrors.hasInternetAccess &&
                'Please tell us if your vehicle can access the internet'
              }
              onValueChange={value =>
                onHasInternetAccessValueChange(value === 'yes')
              }
            >
              <Radio
                id="hasInternetAccess"
                label="Yes"
                value="yes"
                checked={hasInternetAccess === true}
              />
              <Radio
                id="hasNoInternetAccess"
                label="No"
                value="no"
                checked={hasInternetAccess === false}
              />
            </RadioField>
          </SectionContainer>

          <SectionDivider />

          <SectionContainer>
            <SectionHeader Icon={SolarPanelIcon}>
              Do you have solar panels at home?
            </SectionHeader>

            <StyledP>
              Charge Anytime works with solar panels, as long as you have a
              compatible charger.
              <br />
              View our{' '}
              <TextLink opensInNewWindow href={COMPATIBLE_EV_CHARGERS_LINK}>
                list of solar compatible chargers.
              </TextLink>
            </StyledP>
            <RadioField
              label=""
              name="hasSolarPanels"
              error={
                formErrors.hasSolarPanels &&
                'Please tell us if you have solar panels at home'
              }
              onValueChange={value =>
                onHasSolarPanelsValueChange(value === 'yes')
              }
            >
              <Radio
                id="hasSolarPanels"
                label="Yes"
                value="yes"
                checked={hasSolarPanels === true}
              />
              <Radio
                id="hasNoSolarPanels"
                label="No"
                value="no"
                checked={hasSolarPanels === false}
              />
            </RadioField>
          </SectionContainer>

          <ButtonsContainer>
            <AnalyticsClick
              name={
                EV.ELIGIBILITY
                  .EVS_OVO_CHARGE_ANYTIME_ELIGIBILITY_HOME_SET_UP_BACK_CLICKED
              }
            >
              <ElementSecondaryCTAButton
                type="button"
                data-event-name={
                  'Click-EVS - EVS OVO Charge Anytime D2V Home Set Up back clicked [Orion Web]'
                }
                onClick={() =>
                  history.push(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER)
                }
              >
                Back
              </ElementSecondaryCTAButton>
            </AnalyticsClick>
            <StyledElementPrimaryCTAButton
              type="submit"
              data-testid="submit-button"
            >
              Check available plans
              <ButtonIcon src={ChevronRight} alt="" aria-hidden="true" />
            </StyledElementPrimaryCTAButton>
          </ButtonsContainer>
        </Card>
      </form>
    </PageWithNavigation>
  );
};
